import LabelTableCell from 'Components/LabelTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import type { Catalog } from 'Services/ProductManagement/Features/Catalog/types'
import type { ParameterGroup } from 'Services/ProductManagement/Features/Parameter/types'
import type { Tariff } from 'Services/ProductManagement/Features/Tariff/tariffSlice'
import { datetimeFormatter, getValueFromOptions, showValueOrHyphen } from 'Util/formatters'
import getTranslation from 'Util/getTranslation'
import {
  CircularProgress,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer
} from '@mui/material'
import { t } from 'ttag'

const StyledTableCell = styled(LabelTableCell)`
  width: 40%;
`

interface TariffCatalogDetailsProps {
  catalog: Catalog
  tariff: Tariff
  singleLoading: boolean
  getProductCount: (productIds: string[]) => number | string
  salesChannelParameterGroup: ParameterGroup | null
  isSalesChannelParameterGroupLoading: boolean
}

function TariffCatalogDetails({
  catalog,
  tariff,
  singleLoading,
  getProductCount,
  salesChannelParameterGroup,
  isSalesChannelParameterGroupLoading
}: Readonly<TariffCatalogDetailsProps>) {
  const salesChannelOptions =
    salesChannelParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  return (
    <WithLoader loading={singleLoading}>
      <TableContainer component={Paper}>
        <Table sx={{ td: { border: 'none' } }} aria-label={t`Catalog details table`}>
          <TableBody>
            <StyledTableRow>
              <StyledTableCell>{t`Name`}</StyledTableCell>
              <TableCell>{catalog.name}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Catalog code`}</StyledTableCell>
              <TableCell>{catalog.catalog_code}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Description`}</StyledTableCell>
              <TableCell>{showValueOrHyphen(catalog.description)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Parameter group`}</StyledTableCell>
              <TableCell>
                {isSalesChannelParameterGroupLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  getValueFromOptions(catalog.sales_channel, salesChannelOptions, '-')
                )}
              </TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Catalog owner`}</StyledTableCell>
              <TableCell>{showValueOrHyphen(catalog.catalog_owner)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Products amount`}</StyledTableCell>
              <TableCell>{getProductCount(catalog.product_ids || [])}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Tariff`}</StyledTableCell>
              <TableCell>{tariff.tariff_code}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Active from`}</StyledTableCell>
              <TableCell>{datetimeFormatter(tariff.active_period.valid_from)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Active to`}</StyledTableCell>
              <TableCell>{datetimeFormatter(tariff.active_period.valid_until)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Created`}</StyledTableCell>
              <TableCell>{datetimeFormatter(new Date(catalog.created_timestamp))}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Modified`}</StyledTableCell>
              <TableCell>{datetimeFormatter(new Date(catalog.modified_timestamp))}</TableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </WithLoader>
  )
}

export default TariffCatalogDetails
